import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CalendarCheck, CheckCircle2, Loader2, LogOut, Lock, Mail, Phone, RefreshCw, Undo2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "office_token";

const fmtChoice = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} at ${timeStr}`;
};

const fmtTs = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const LoginCard = ({ onToken }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/office/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onToken(data.token);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[var(--brand-cream)]">
      <div data-testid="office-login-card" className="w-full max-w-sm bg-white border border-[var(--brand-gold-soft)] shadow-md p-8">
        <img src="/images/logo.png" alt="All-In Precision Wellness" className="h-16 w-16 mx-auto" />
        <h1 className="mt-4 text-center font-serif-brand text-2xl font-medium text-[var(--brand-navy)]">
          Office sign-in
        </h1>
        <p className="mt-1 text-center text-sm text-[var(--brand-muted)]">
          Appointment requests dashboard
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="office-pw">Office password</Label>
            <Input
              id="office-pw"
              data-testid="office-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1.5"
            />
          </div>
          {error && (
            <p data-testid="office-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">
              {error}
            </p>
          )}
          <button
            data-testid="office-login-button"
            type="submit"
            disabled={busy || !password}
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white font-semibold py-3 hover:bg-[var(--brand-navy-deep)] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Lock className="h-4 w-4" aria-hidden="true" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) =>
  status === "CONFIRMED" ? (
    <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold uppercase tracking-wide px-2.5 py-1">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Confirmed
    </span>
  ) : (
    <span className="inline-flex items-center bg-[var(--brand-cream)] border border-[var(--brand-gold-soft)] text-[var(--brand-navy)] text-xs font-semibold uppercase tracking-wide px-2.5 py-1">
      Needs confirmation
    </span>
  );

const RequestCard = ({ req, busyId, onConfirm, onUndo }) => {
  const busy = busyId === req.request_id;
  const confirmed = req.status === "CONFIRMED";
  return (
    <div
      data-testid={`office-request-card-${req.request_id}`}
      className="bg-white border border-[var(--brand-gold-soft)] p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif-brand text-xl font-medium text-[var(--brand-navy)]">
            {req.first_name} {req.last_name}
          </p>
          <p className="mt-0.5 text-sm text-[var(--brand-muted)]">
            {req.visit_type} &bull; requested {fmtTs(req.ts)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {req.deposit_paid && (
            <span className="inline-flex items-center bg-[var(--brand-navy)] text-white text-xs font-semibold uppercase tracking-wide px-2.5 py-1">
              $25 deposit paid
            </span>
          )}
          <StatusPill status={req.status} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <a href={`tel:${req.phone}`} className="inline-flex items-center gap-1.5 text-[var(--brand-navy)] font-medium">
          <Phone className="h-3.5 w-3.5 text-[var(--brand-gold)]" aria-hidden="true" /> {req.phone}
        </a>
        <a href={`mailto:${req.email}`} className="inline-flex items-center gap-1.5 text-[var(--brand-navy)] font-medium">
          <Mail className="h-3.5 w-3.5 text-[var(--brand-gold)]" aria-hidden="true" /> {req.email}
        </a>
      </div>
      {confirmed ? (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-emerald-900">
              {fmtChoice(req.confirmed_date, req.confirmed_time)}
            </p>
            <p className="mt-0.5 text-xs text-emerald-800">
              {req.confirm_email_sent
                ? "Patient was emailed this confirmation."
                : "Heads up: the confirmation email could not be sent — please call the patient."}
            </p>
          </div>
          <button
            data-testid={`office-undo-button-${req.request_id}`}
            onClick={() => onUndo(req)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-900 text-sm font-semibold px-3 py-2 bg-white hover:bg-emerald-100 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Undo2 className="h-4 w-4" aria-hidden="true" />}
            Undo
          </button>
        </div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {[1, 2].map((n) => (
            <div key={n} className="border border-[var(--brand-gold-soft)] bg-[var(--brand-cream)] p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-gold)]">
                Choice {n}
              </p>
              <p className="mt-1 font-medium text-[var(--brand-navy)]">
                {fmtChoice(req[`choice${n}_date`], req[`choice${n}_time`])}
              </p>
              <button
                data-testid={`office-confirm-${n}-button-${req.request_id}`}
                onClick={() => onConfirm(req, n)}
                disabled={busy}
                className="mt-3 inline-flex items-center gap-1.5 bg-[var(--brand-navy)] text-white text-sm font-semibold px-4 py-2 hover:bg-[var(--brand-navy-deep)] transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CalendarCheck className="h-4 w-4" aria-hidden="true" />}
                Confirm this time
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FILTERS = [
  { key: "requested", label: "Needs confirmation" },
  { key: "confirmed", label: "Confirmed" },
  { key: "all", label: "All" },
];

export default function OfficeDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("requested");
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState("");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setRequests(null);
  }, []);

  const load = useCallback(async (tok) => {
    try {
      const { data } = await axios.get(`${API}/office/requests`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setRequests(data.requests);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const act = async (req, path, body, message) => {
    setBusyId(req.request_id);
    setNotice("");
    try {
      await axios.post(`${API}/office/requests/${req.request_id}/${path}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice(message);
      await load(token);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(errText(e));
    } finally {
      setBusyId(null);
    }
  };

  const confirm = (req, choice) =>
    act(req, "confirm", { choice },
      `Confirmed ${req.first_name} ${req.last_name} — the patient has been emailed.`);
  const undo = (req) => act(req, "unconfirm", {}, "Confirmation undone.");

  if (!token) {
    return (
      <>
        <Seo
          title="Office — All-In Precision Wellness"
          description="Staff access to appointment requests."
          path="/office"
          noindex
        />
        <LoginCard onToken={setToken} />
      </>
    );
  }

  const visible = (requests || []).filter((r) =>
    filter === "all" ? true : filter === "confirmed" ? r.status === "CONFIRMED" : r.status !== "CONFIRMED");

  return (
    <div className="min-h-screen bg-[var(--brand-bg)]">
      <Seo
        title="Office — All-In Precision Wellness"
        description="Staff access to appointment requests."
        path="/office"
        noindex
      />
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[var(--brand-gold-soft)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/images/logo.png" alt="All-In Precision Wellness" className="h-11 w-11 object-contain" />
            <span className="font-serif-brand text-lg font-medium text-[var(--brand-navy)] truncate">
              Appointment Requests
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="office-refresh-button"
              onClick={() => load(token)}
              aria-label="Refresh"
              className="p-2 border border-[var(--brand-gold-soft)] text-[var(--brand-navy)] bg-white hover:bg-[var(--brand-cream)] transition-colors"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              data-testid="office-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 border border-[var(--brand-gold-soft)] text-[var(--brand-navy)] text-sm font-semibold px-3 py-2 bg-white hover:bg-[var(--brand-cream)] transition-colors"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`office-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-sm font-semibold px-4 py-2 border transition-colors ${
                filter === f.key
                  ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]"
                  : "bg-white text-[var(--brand-navy)] border-[var(--brand-gold-soft)] hover:bg-[var(--brand-cream)]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {notice && (
          <p data-testid="office-notice" className="mt-4 text-sm font-medium text-[var(--brand-navy)] bg-[var(--brand-cream)] border border-[var(--brand-gold-soft)] p-3">
            {notice}
          </p>
        )}
        <div className="mt-6 space-y-4">
          {requests === null ? (
            <p className="text-[var(--brand-muted)]">Loading&hellip;</p>
          ) : visible.length === 0 ? (
            <p data-testid="office-empty-state" className="text-[var(--brand-muted)]">
              No requests here right now.
            </p>
          ) : (
            visible.map((r) => (
              <RequestCard key={r.request_id} req={r} busyId={busyId} onConfirm={confirm} onUndo={undo} />
            ))
          )}
        </div>
        <p className="mt-8 text-xs text-[var(--brand-muted)]">
          Confirming a time emails the patient and stops the 24-hour reminder for that request.
        </p>
      </main>
    </div>
  );
}
